import { useCallback, useEffect } from 'react';

import { Select } from 'ui';

import { RightSection } from './RightSection';
import { CreatableSearchableSelect } from './components/CreatableSearchableSelect';
import { useSearchableInput } from './hooks';
import { SearchableSelectWithComboBox, SearchableSelectWithOptionData } from './types';

const WithCombobox = ({
  useFetchSource,
  initialData,
  value,
  data,
  dataRef,
  onChange,
  onSearchChange,
  ...rest
}: SearchableSelectWithComboBox) => {
  const {
    isLoading,
    fetchedData,
    addCreatedOption,
    onSearchChange: onSearch,
    searchValue,
  } = useSearchableInput({
    useFetchSource,
    type: 'select',
    initialData,
  });

  const handleSearchChange = useCallback(
    (searchValue?: string) => {
      onSearch?.(searchValue);
      if (searchValue) {
        onSearchChange?.(searchValue);
      }
    },
    [onSearch, onSearchChange]
  );

  /**
   * used to bind the data to the ref so that it can be accessed outside of the component if needed
   */
  useEffect(() => {
    if (dataRef) {
      dataRef.current = data ?? fetchedData ?? [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fetchedData]);

  return (
    <CreatableSearchableSelect
      isLoading={isLoading}
      value={value}
      data={data ?? fetchedData ?? []}
      onChange={(value, option) => onChange?.(value ?? '', option)}
      rightSection={
        <RightSection
          isLoading={isLoading}
          value={searchValue ?? value}
          onChange={() => {
            onChange?.(null);
            handleSearchChange(undefined);
          }}
        />
      }
      onSearchChange={handleSearchChange}
      searchValue={searchValue}
      addCreatedOption={addCreatedOption}
      {...rest}
    />
  );
};

const SearchableSelect = ({
  data,
  useFetchSource,
  value,
  onChange,
  dataRef,
  initialData,
  ...rest
}: SearchableSelectWithOptionData) => {
  const { isLoading, fetchedData, props, onSearchChange, searchValue } = useSearchableInput({
    useFetchSource,
    type: 'select',
    initialData,
  });
  /**
   * used to bind the data to the ref so that it can be accessed outside of the component if needed
   */
  useEffect(() => {
    if (dataRef) {
      dataRef.current = data ?? fetchedData ?? [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fetchedData]);

  return (
    <Select
      value={value}
      data={data ?? fetchedData ?? []}
      onChange={(value, option) => onChange?.(value ?? '', option)}
      {...rest}
      onSearchChange={onSearchChange}
      searchValue={searchValue}
      rightSection={
        <RightSection isLoading={isLoading} value={value} onChange={() => onChange?.(null)} />
      }
      {...props}
    />
  );
};
SearchableSelect.Combobox = WithCombobox;

export { SearchableSelect };
